import {
  loginSecurityService,
  getCurrentUser,
  createUserFirstAccess,
} from '@/common/securityserver';
import { isCookieAuth } from '@/common/authStrategy';
import secureSessionStore from '@/common/secureSessionStore';
import $router from "../router/router";

const SESSION_TOLERANCE_MS = 3600000;
const NON_COOKIE_SESSION_STORAGE_KEY = 'account_session';
const DEFAULT_PROFILE_PICTURE_URL =
  'https://www.pngfind.com/pngs/m/110-1102775_download-empty-profile-hd-png-download.png';

const state = {
  session: {},
};

const getters = {
  user: (state) => state.session,
};

function isIosSafari(){
  return /iP(ad|od|hone)/i.test(navigator.userAgent) && /WebKit/i.test(navigator.userAgent) && !(/(CriOS|FxiOS|OPiOS|mercury)/i.test(navigator.userAgent));
}

function isChrome() {
  return (/Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor)) || /ANDROID/i.test(navigator.userAgent.toUpperCase());
}

function isTermsRouteActive() {
  return $router.currentRoute && $router.currentRoute.name === 'termos';
}

function readPersistedNonCookieSession() {
  try {
    const raw = sessionStorage.getItem(NON_COOKIE_SESSION_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_e) {
    return null;
  }
}

function clearPersistedNonCookieSession() {
  try {
    sessionStorage.removeItem(NON_COOKIE_SESSION_STORAGE_KEY);
  } catch (_e) {
    void 0;
  }
}

function persistNonCookieSession(formattedUser) {
  try {
    sessionStorage.setItem(NON_COOKIE_SESSION_STORAGE_KEY, JSON.stringify(formattedUser));
  } catch (_e) {
    void 0;
  }
}

function isSessionWithinTolerance(user) {
  if (!user || user.dtcriacao == null) return false;
  const difference = new Date().getTime() - user.dtcriacao;
  return difference <= SESSION_TOLERANCE_MS;
}

const actions = {
  async serviceLogin(context, user) {

    user.browser = isIosSafari() ? 'Safari' : isChrome() ? 'Chrome' : '';

    const tokenSecurityService = await loginSecurityService(user);

    if (tokenSecurityService.error) {

      let errorMsg = tokenSecurityService.message;

      if (errorMsg == "O Termo de Uso não foi aceito.") {
        context.commit("auth", user);
        await $router.push("/termos");
        return;
      } else if (errorMsg == "ACCESS_VALIDATION_ERROR_MESSAGE") {
        await $router.push("/userNotEnableToAccess");
        return;
      } else if (errorMsg == "Usuário bloqueado.") {
        await $router.push("/userLocked");
        return;
      } else if (context.state.appCode === 'eu_protegido' && (errorMsg === "USER_SOCIAL_ID_NOT_EXISTS" || errorMsg === "VALID_ACCOUNT_BUT_NO_USER" || errorMsg === "ACCOUNT_LOGIN_NOT_FOUND")){
        if(!user.login){
          await $router.push('primeiro-acesso');
          return
        }else{
          await context.dispatch('createUserFirstAccess', user);
          return;
        }
      } else if(errorMsg == 'FINANCIAL_GROUP_NOT_PRESENT_OR_ACTIVE' || errorMsg == 'RH_PROTEGIDO_IS_DISABLED_APP_CONFIGURATION'){
        errorMsg = "Acesso negado. Por favor, contate o seu RH.";
      } else if (errorMsg == 'INVALID_CONTRACT_PERMISSION') {
        errorMsg = "Por favor, entre em contato com seu Gerente de Relacionamento para verificar configurações em seu contrato.";
      } else {
        errorMsg = "Acesso Inválido"
      }

      context.commit("showmsg", {
        text: errorMsg,
        type: "error"
      });

      return { error: true, text: errorMsg};

    }else{
      if(context.state.appCode === 'eu_protegido') {
        context.commit("email", user.login);
        context.commit("password", user.password);
      }
      context.commit("tinnyToken", tokenSecurityService.tinnyToken);
      context.commit("token", tokenSecurityService.token);
      await $router.push("/serviceLoginCallback");
    }
  },
  async login(context, data) {

    const tokenSecurityService = await loginSecurityService(data);
    if (tokenSecurityService.error) {
      return { error: true, text: tokenSecurityService.message };
    }else{
      return await context.dispatch('createSession', tokenSecurityService);
    }
  },
  async createSession(context, tokenSecurityService){
    const user = await getCurrentUser(tokenSecurityService);
    if (user && user.error) {
      return { error: true, text: user.message };
    }
    user.tokenSecurityService = tokenSecurityService;
    user.profilePicture = DEFAULT_PROFILE_PICTURE_URL;

    const formattedSessionData = {
      name: user.name,
      email: user.email,
      profilePicture: user.profilePicture,
      tokenSecurityService: user.tokenSecurityService,
    };

    await context.dispatch('startSession', formattedSessionData);
    return true;
  },

  async restoreSessionFromCookie(context) {
    let user;
    try {
      user = await getCurrentUser({ cookieAuth: true });
    } catch (_e) {
      return false;
    }
    if (!user || user.error) {
      return false;
    }
    user.tokenSecurityService = { cookieAuth: true };
    user.profilePicture = DEFAULT_PROFILE_PICTURE_URL;

    const formattedSessionData = {
      name: user.name,
      email: user.email,
      profilePicture: user.profilePicture,
      tokenSecurityService: user.tokenSecurityService,
    };

    await context.dispatch('startSession', formattedSessionData);
    return true;
  },

  async startSession(context, sessionData) {
    const formattedUser = {
      nome: sessionData.name,
      email: sessionData.email,
      img: sessionData.profilePicture,
      dtcriacao: new Date().getTime(),
      tokenSecurityService: sessionData.tokenSecurityService,
    };

    secureSessionStore.setSession(formattedUser);
    context.commit('setSession', formattedUser);
    if (!isCookieAuth(context.state.appCode)) {
      persistNonCookieSession(formattedUser);
    }
  },
  logout(context) {
    secureSessionStore.clear();
    clearPersistedNonCookieSession();
    try {
      localStorage.removeItem('auth_user');
    } catch (_e) {
      void 0;
    }
    context.commit('updateMenuState', {
      left: false,
      leftmini: false,
      right: false,
    });
    context.commit('setSession', {});
  },
  async checkAuthentication(context) {
    const appCode = context.state.appCode;
    let user = secureSessionStore.getSession();

    if (!user.dtcriacao && !isCookieAuth(appCode)) {
      const persisted = readPersistedNonCookieSession();
      if (persisted && persisted.dtcriacao) {
        user = persisted;
        secureSessionStore.setSession(user);
      }
    }

    if (isSessionWithinTolerance(user)) {
      context.commit('setSession', user);
      return true;
    }

    if (user && user.dtcriacao) {
      secureSessionStore.clear();
      if (!isCookieAuth(appCode)) {
        clearPersistedNonCookieSession();
      }
    }

    if (isCookieAuth(appCode)) {
      if (isTermsRouteActive()) {
        return false;
      }
      return await context.dispatch('restoreSessionFromCookie');
    }

    return false;
  },

  async createUserFirstAccess(context, createUserDto) {
    await createUserFirstAccess(createUserDto.user, createUserDto.recaptchaToken).then(user => {
      user.password = createUserDto.user.password;
      return loginSecurityService(user).then(async response => {
        if (response.error && response.message === 'O Termo de Uso não foi aceito.') {
          context.commit('auth', createUserDto.user);
          return $router.push('/termos');
        }
        context.commit("tinnyToken", response.tinnyToken);
        context.commit("token", response.token);
        await getCurrentUser(response.token).then(currentResponse => {
          context.commit('token', response.token);
          $router.push('/serviceLoginCallback');
        });
      })
    })
    .catch(error => {
      let message = "Erro ao criar usuário!";
      if (error.response.data.message && error.response.data.message == 'User Login Already Exists') {
        message = "Email informado já foi cadastrado";
      } else if (error.response.data.message && error.response.data.message == 'User Invalid CPF') {
        message = "CPF informado é inválido";
      } else if (error.response.data.message && error.response.data.message == 'LDAP Password Policy Exception') {
        message = "Senha não atende os requisitos da Política de Senha";
      } else if (error.response.data.message && error.response.data.message == 'Validation failed for object=\'userFirstAccess\'. Error count: 2') {
        message = 'Login informado não encontrado.';
      }

      context.commit("showmsg", {
        text: message,
        type: "error"
      });

    });
  }

};

const mutations = {
  setSession(state, data) {
    state.session = data;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
